import React from 'react';
import { Users, Loader2 } from 'lucide-react';
import { moduleApi } from '../../lib/api';
import { formatMinutes } from '../../lib/tempsMission';

interface LigneSalarie {
    id: string; nom: string; rate_percent: number; missions: number; ca_ht: number; commission: number;
    minutes_reelles: number; minutes_prevues: number; missions_pointees: number;
}

const iso = (d: Date) => d.toISOString().split('T')[0];
const fmtEuro = (n: number) => n.toLocaleString('fr-FR', { style: 'currency', currency: 'EUR' });

/**
 * Commissions et temps passé par salarié (2026-10-18, audit concurrents : Fresha, Booksy). Pour une période,
 * chaque salarié : missions terminées, chiffre d'affaires HT, commission (taux réglé ici) et temps réellement
 * passé (pointage « Commencer / Terminer » de sa fiche). Une mission à plusieurs se partage à parts égales.
 */
export function CommissionsPage() {
    const maintenant = new Date();
    const [from, setFrom] = React.useState(iso(new Date(Date.UTC(maintenant.getFullYear(), maintenant.getMonth(), 1))));
    const [to, setTo] = React.useState(iso(maintenant));
    const [lignes, setLignes] = React.useState<LigneSalarie[]>([]);
    const [loading, setLoading] = React.useState(true);
    const [erreur, setErreur] = React.useState('');
    const [taux, setTaux] = React.useState<Record<string, string>>({});
    const [enregistre, setEnregistre] = React.useState('');

    const charger = React.useCallback(async () => {
        setLoading(true); setErreur('');
        try {
            const res: any = await moduleApi.getPrestationCommissions(from, to);
            if (res?.error) throw new Error(res.error);
            const l: LigneSalarie[] = Array.isArray(res?.salaries) ? res.salaries : [];
            setLignes(l);
            setTaux(Object.fromEntries(l.map((s) => [s.id, String(s.rate_percent || 0)])));
        } catch (e: any) { setErreur(e?.message || 'Chargement impossible.'); }
        finally { setLoading(false); }
    }, [from, to]);
    React.useEffect(() => { charger(); }, [charger]);

    const enregistrerTaux = async (s: LigneSalarie) => {
        const valeur = Number(String(taux[s.id] ?? '').replace(',', '.'));
        if (!Number.isFinite(valeur) || valeur < 0 || valeur > 100) { setErreur('Le taux doit être compris entre 0 et 100 %.'); return; }
        if (valeur === s.rate_percent) return;
        setErreur('');
        try {
            const res: any = await moduleApi.setPrestationCommissionRate(s.id, valeur);
            if (res?.error) throw new Error(res.error);
            setEnregistre(s.nom);
            await charger();
        } catch (e: any) { setErreur(e?.message || "Le taux n'a pas pu être enregistré."); }
    };

    const totalCommission = lignes.reduce((n, s) => n + s.commission, 0);
    const totalCa = lignes.reduce((n, s) => n + s.ca_ht, 0);
    const champ = 'bg-surface border border-border rounded-lg px-3 py-1.5 text-sm text-content';

    return (
        <div className="p-6 max-w-5xl mx-auto space-y-5">
            <div>
                <h1 className="text-xl font-black text-content flex items-center gap-2.5"><Users size={22} className="text-brand-600 dark:text-brand-400" /> Commissions et temps passé</h1>
                <p className="text-sm text-content-muted mt-1">Pour chaque salarié : les missions terminées sur la période, le chiffre d'affaires HT, sa commission et le temps réellement passé.</p>
            </div>
            <div className="flex flex-wrap items-end gap-3">
                <label className="text-xs font-bold text-content-muted">Du<input type="date" className={`${champ} block mt-1`} value={from} onChange={(e) => setFrom(e.target.value)} /></label>
                <label className="text-xs font-bold text-content-muted">Au<input type="date" className={`${champ} block mt-1`} value={to} onChange={(e) => setTo(e.target.value)} /></label>
            </div>
            {erreur && <p className="text-sm text-red-500 font-semibold">{erreur}</p>}
            {enregistre && !erreur && <p className="text-xs text-green-600 dark:text-green-400 font-semibold">Taux enregistré pour {enregistre}.</p>}
            {loading ? (
                <div className="flex justify-center py-16"><Loader2 size={26} className="animate-spin text-content-muted" /></div>
            ) : lignes.length === 0 ? (
                <p className="text-sm text-content-muted text-center py-12">Aucun salarié pour le moment.</p>
            ) : (
                <div className="border border-border rounded-xl overflow-x-auto">
                    <table className="w-full text-sm">
                        <thead className="bg-surface-soft text-[10px] font-black uppercase tracking-widest text-content-muted">
                            <tr>
                                <th className="text-left px-4 py-3">Salarié</th>
                                <th className="text-left px-4 py-3">Taux</th>
                                <th className="text-right px-4 py-3">Missions</th>
                                <th className="text-right px-4 py-3">CA HT</th>
                                <th className="text-right px-4 py-3">Commission</th>
                                <th className="text-right px-4 py-3">Temps passé</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-border">
                            {lignes.map((s) => (
                                <tr key={s.id}>
                                    <td className="px-4 py-3 font-bold text-content">{s.nom}</td>
                                    <td className="px-4 py-3">
                                        <span className="inline-flex items-center gap-1">
                                            <input inputMode="decimal" className={`${champ} w-20 text-right`} value={taux[s.id] ?? ''} onChange={(e) => setTaux((t) => ({ ...t, [s.id]: e.target.value }))}
                                                onBlur={() => enregistrerTaux(s)} onKeyDown={(e) => { if (e.key === 'Enter') (e.target as HTMLInputElement).blur(); }} aria-label={`Taux de commission de ${s.nom}`} />
                                            <span className="text-content-muted">%</span>
                                        </span>
                                    </td>
                                    <td className="px-4 py-3 text-right">{s.missions}</td>
                                    <td className="px-4 py-3 text-right">{fmtEuro(s.ca_ht)}</td>
                                    <td className="px-4 py-3 text-right font-black text-content">{fmtEuro(s.commission)}</td>
                                    <td className="px-4 py-3 text-right text-content-muted">
                                        {s.missions_pointees > 0 ? formatMinutes(s.minutes_reelles) : '—'}
                                        {s.missions_pointees > 0 && <span className="block text-[11px]">prévu {formatMinutes(s.minutes_prevues)}</span>}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot className="bg-surface-soft text-sm font-black text-content">
                            <tr><td className="px-4 py-3" colSpan={3}>Total</td><td className="px-4 py-3 text-right">{fmtEuro(totalCa)}</td><td className="px-4 py-3 text-right">{fmtEuro(totalCommission)}</td><td /></tr>
                        </tfoot>
                    </table>
                </div>
            )}
            <p className="text-[11px] text-content-muted">Le chiffre d'affaires est celui des missions marquées « terminées » dans le planning (frais inclus), hors taxes. Le temps passé vient des boutons Commencer et Terminer du salarié ; « prévu » est la durée du planning.</p>
        </div>
    );
}
